<script lang="ts" setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import type { PropType } from 'vue'
import { debounce } from 'radashi'
import Plyr from 'plyr'
import Hls from 'hls.js/dist/hls.light.min.js'
import {
	useBreakpoints,
	setIntercomDisplay,
	getCurrentGaPageName,
} from '@/lib/utils'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'

const props = defineProps({
	mediaList: {
		type: Object as PropType<
			{
				type: string
				content: string
				id?: string
				preview?: string
			}[]
		>,
		default: () => [],
	},
	galleryIndex: {
		type: Number,
		default: 0,
	},
	isFullScreen: {
		type: Boolean,
		default: false,
	},
	isShowFullMediaDialog: {
		type: Boolean,
		default: false,
	},
	isGallery: {
		type: Boolean,
		default: false,
	},
	imgAltText: {
		type: String,
		default: 'Image',
	},
})

const isSlideTransition = ref(false)
const localIndex = ref(0)
const mainWidth = ref(0)
const mainWidthHandler = ref<any>(null)
const mobileViewHeightHandler = ref<any>(null)
const touchXDown = ref(null)
const touchYDown = ref(null)
const videoPlayer = ref<any>(null)
const currentMainRef = ref<HTMLElement | null>(null)
const loadingSkeletonNumber = 3
const listIsLoadingLocal = ref<boolean[]>(
	Array(loadingSkeletonNumber).fill(true),
)

const { isLargeOld: isDesktop } = useBreakpoints()
const emit = defineEmits(['setGalleryIndex', 'showFullScreenDialog'])

const currentIndex = computed(() => {
	return props.isGallery ? props.galleryIndex : localIndex.value
})

const videoIndex = computed(() => {
	return props.mediaList.findIndex(item => item.type.includes('video'))
})

const isNextEnable = computed(() => {
	return currentIndex.value < props.mediaList.length - 1
})

const isPrevEnable = computed(() => {
	return currentIndex.value
})

const currentImageTranslateX = computed(() => {
	return `transform: translateX(-${currentIndex.value * mainWidth.value}px)`
})

const pagination = computed(() => {
	return `${currentIndex.value + 1} / ${props.mediaList.length}`
})

const isMobileTouchMode = computed(() => {
	return props.isFullScreen && props.isShowFullMediaDialog && !isDesktop.value
})

function setCurrentIndex(index: number) {
	if (props.isGallery) {
		emit('setGalleryIndex', index)
	} else {
		localIndex.value = index
	}
}

function setMainWidth() {
	if (currentMainRef.value) {
		nextTick(() => {
			if (!currentMainRef.value) return
			mainWidth.value = isDesktop.value
				? currentMainRef.value.getBoundingClientRect().width
				: document.documentElement.clientWidth
		})
	}
}

function setMobileViewHeight() {
	const vh = window.innerHeight * 0.01
	document.documentElement.style.setProperty('--vh', `${vh}px`)
}

function toTargetImage(index: number, isTransition = true) {
	if (currentIndex.value === index) return
	if (index < 0 || index > props.mediaList.length - 1) return
	sendGaEvent()
	isSlideTransition.value = isTransition
	videoControl(index)
	setCurrentIndex(index)
}

function videoControl(index: number) {
	if (props.isShowFullMediaDialog && videoIndex.value !== -1) {
		if (videoIndex.value === index) {
			// videoPlayer.value.play()
		} else {
			videoPlayer.value.pause()
		}
	}
}

function handleTouchStart(evt: TouchEvent) {
	const firstTouch = getTouches(evt)[0]
	touchXDown.value = firstTouch.clientX
	touchYDown.value = firstTouch.clientY
}

function handleTouchMove(evt: TouchEvent) {
	if (!touchXDown.value || !touchYDown.value) return

	const xUp = evt.touches[0].clientX
	const yUp = evt.touches[0].clientY
	const xDiff = touchXDown.value - xUp
	const yDiff = touchYDown.value - yUp

	touchXDown.value = null
	touchYDown.value = null

	if (Math.abs(xDiff) > Math.abs(yDiff)) {
		if (xDiff > 0 && isNextEnable.value) {
			toTargetImage(currentIndex.value + 1)
			return
		}

		if (xDiff < 0 && isPrevEnable.value) {
			toTargetImage(currentIndex.value - 1)
		}
	}
}

function getTouches(evt: any) {
	return evt.touches || evt.originalEvent.touches
}

function showFullScreenDialog() {
	if (props.isFullScreen || !props.isGallery) return
	emit('showFullScreenDialog')
	sendGaEvent()
}

function initVideo() {
	if (!props.isShowFullMediaDialog || videoIndex.value === -1) return

	handleMobileIntercomDisplay(false)

	const videoSource = props.mediaList[videoIndex.value].content
	const videoEl = document.getElementById('video') as HTMLVideoElement
	const plyrConfg = {
		controls: [
			'play-large',
			'play',
			'progress',
			'current-time',
			'mute',
			'fullscreen',
		],
		fullscreen: {
			enabled: true,
			fallback: true,
			iosNative: true,
		},
	}

	videoPlayer.value = new Plyr(videoEl, plyrConfg)

	if (Hls.isSupported()) {
		const hls = new Hls({
			maxBufferLength: 2,
			maxBufferSize: 1 * 1000 * 1000, // 1MB
		})
		hls.loadSource(videoSource)
		hls.attachMedia(videoEl)
	} else if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
		videoEl.src = videoSource
	}
}

function handleMobileIntercomDisplay(value: boolean) {
	if (!isDesktop.value) {
		setIntercomDisplay(value)
	}
}

function setLoadingItemDone(index: number) {
	listIsLoadingLocal.value[index] = false
}

function sendGaEvent() {
	if (getCurrentGaPageName() === GaGeneralPageName.MercariDetail) {
		createGAEvent(GaGeneralEvent.ButtonClick, {
			page_name: GaGeneralPageName.MercariDetail,
			button_name: 'View Image',
		})
	}
}

watch(
	() => props.mediaList,
	() => {
		if (props.mediaList.length) {
			listIsLoadingLocal.value = Array(props.mediaList.length).fill(true)
		}

		nextTick(() => {
			setMainWidth()
			setMobileViewHeight()
		})
	},
	{ immediate: true },
)

onMounted(() => {
	initVideo()
	mainWidthHandler.value = debounce({ delay: 200 }, setMainWidth)
	mobileViewHeightHandler.value = debounce({ delay: 200 }, setMobileViewHeight)

	window.addEventListener('resize', mainWidthHandler.value, true)
	window.addEventListener('resize', mobileViewHeightHandler.value, true)
})

onBeforeUnmount(() => {
	if (videoPlayer.value) {
		setIntercomDisplay(true)
		videoPlayer.value.destroy()
	}
	window.removeEventListener('resize', mainWidthHandler.value, true)
	window.removeEventListener('resize', mobileViewHeightHandler.value, true)
})
</script>

<template>
	<div
		:class="[
			'bs-media-slideshow',
			{ 'bs-media-slideshow-full-screen': isFullScreen },
		]"
		v-cloak
	>
		<div class="bs-media-slideshow__side">
			<template v-if="mediaList.length">
				<div
					class="bs-media-slideshow__side-item"
					v-for="(media, index) in mediaList"
					:key="media.id || index"
					@click="toTargetImage(index, false)"
				>
					<img
						:src="media.type.includes('image') ? media.content : media.preview"
						:class="{ active: index === currentIndex }"
						:alt="imgAltText"
						class="bs-media-slideshow__side-item-image"
						loading="lazy"
						@load="setLoadingItemDone(index)"
					/>
					<div
						class="bs-media-slideshow__play"
						v-if="media.type.includes('video')"
					>
						<i class="bs-icon">
							<span class="bs-media-slideshow__play-icon"></span>
						</i>
					</div>
					<div
						class="bs-media-slideshow__side-item-skeleton"
						v-if="listIsLoadingLocal[index]"
					>
						<span class="bs-media-slideshow__skeleton"></span>
					</div>
				</div>
			</template>
			<template v-else>
				<div
					class="bs-media-slideshow__side-item"
					v-for="(item, index) in loadingSkeletonNumber"
					:key="index"
				>
					<div class="bs-media-slideshow__side-item-skeleton">
						<span class="bs-media-slideshow__skeleton"></span>
					</div>
				</div>
			</template>
		</div>
		<div
			:class="[
				'bs-media-slideshow__main',
				{ 'full-height': isMobileTouchMode },
			]"
		>
			<div class="bs-media-slideshow__main-content" ref="currentMainRef">
				<div class="bs-media-slideshow__main-content-viewbox">
					<div
						class="bs-media-slideshow__main-content-list"
						:class="{ transition: isSlideTransition }"
						:style="currentImageTranslateX"
						@touchstart="handleTouchStart"
						@touchmove="handleTouchMove"
						@click="showFullScreenDialog"
					>
						<template v-if="mediaList.length">
							<div
								class="bs-media-slideshow__main-content-list-item"
								v-for="(media, index) in mediaList"
								:key="media.id || index"
							>
								<img
									v-if="media.type.includes('image') || !isShowFullMediaDialog"
									:src="
										media.type.includes('image') ? media.content : media.preview
									"
									:alt="imgAltText"
									:data-index="index"
									class="bs-media-slideshow__main-content-list-item-image"
									:loading="index ? 'lazy' : 'eager'"
									:fetchpriority="index ? 'low' : 'high'"
									@load="setLoadingItemDone(index)"
								/>
								<video
									v-if="isShowFullMediaDialog && media.type.includes('video')"
									id="video"
									class="bs-media-slideshow__main-content-list-item-image plyr-js"
									preload="none"
									muted
									playsinline
								></video>
								<div
									class="bs-media-slideshow__play"
									v-if="media.type.includes('video') && !isShowFullMediaDialog"
								>
									<i class="bs-icon">
										<span class="bs-media-slideshow__play-icon"></span>
									</i>
								</div>
								<span
									class="bs-media-slideshow__skeleton"
									v-if="listIsLoadingLocal[index]"
								></span>
							</div>
						</template>
						<template v-else>
							<div
								class="bs-media-slideshow__main-content-list-item"
								v-for="(media, index) in loadingSkeletonNumber"
								:key="index"
							>
								<span class="bs-media-slideshow__skeleton"></span>
							</div>
						</template>
					</div>
				</div>
				<div
					class="bs-media-slideshow__arrow-left"
					v-if="isPrevEnable"
					@click="toTargetImage(currentIndex - 1)"
				>
					<i class="bs-icon">
						<span class="bs-media-slideshow__arrow-left-icon"></span>
					</i>
				</div>
				<div
					class="bs-media-slideshow__arrow-right"
					v-if="isNextEnable"
					@click="toTargetImage(currentIndex + 1)"
				>
					<i class="bs-icon">
						<span class="bs-media-slideshow__arrow-right-icon"></span>
					</i>
				</div>
				<div class="bs-media-slideshow__pagination" v-if="mediaList.length">
					{{ pagination }}
				</div>
			</div>
		</div>
	</div>
</template>
